import React, { useState, useEffect, ReactNode } from "react";

// elements
import Dropdown, { DropdownProps } from "@/shared/Dropdown";
import InputNumber from "@/shared/Input/InputNumber";

// helpers
import { SPACE_SIZES, DEFAULT_SIZES } from "./constants";

// assets
import styles from "@/assets/styles/components/space-dropdown.module.scss";


type SelectedValue = {
  label: string;
  value: string;
  isClosable?: boolean;
}[];

type CustomSizes = {
  width: null | number;
  length: null | number;
};

interface ISpaceSizeDropdown extends Omit<DropdownProps, "onChange"> {
  onChange?: (
    value: SelectedValue | { width: string | number | null, length: string | number | null },
    type?: string
  ) => void;
  children?: ReactNode;
  value?: SelectedValue;
  customSizes?: CustomSizes;
}

function SpaceSizeDropdown(props: ISpaceSizeDropdown) {
  const {
    onChange,
    children,
    value,
    customSizes,
    overlayClassName = "",
    className = "",
    onClick,
    disabled,
    buttonProps
  } = props;

  const [selectedValue, setSelectedValue] = useState<SelectedValue | null>(null);
  const [customSizesSelected, setCustomSizesSelected] = useState<CustomSizes>(DEFAULT_SIZES);

  useEffect(() => {
    if (typeof value === "undefined") {
      return;
    }

    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    if (!customSizes || (!customSizes.length && !customSizes.width)) {
      return;
    }

    setSelectedValue([SPACE_SIZES[3]]);
    setCustomSizesSelected(customSizes);
  }, [customSizes]);

  const isCustom = (selectedValue || []).some(item => item.value === "custom");

  const handleChange = (e: SelectedValue) => {

    const isNewCustomValue = (e || []).some((item) => item.value === "custom");

    if (isNewCustomValue && !isCustom) {
      setSelectedValue(e);
      setCustomSizesSelected(DEFAULT_SIZES);
      onChange && onChange(e);

      return;
    }

    const valueToSet = e.filter((item) => item.value !== "custom");

    setCustomSizesSelected(DEFAULT_SIZES);
    setSelectedValue(valueToSet);

    onChange && onChange(valueToSet);
  };

  const handleCustomChange = (key: string, e: string) => {
    const customToSet = { ...customSizesSelected, [key]: e };
    setCustomSizesSelected(customToSet);
    onChange && onChange(customToSet, "custom");

    setSelectedValue([SPACE_SIZES[3]]);
  };

  const onOpenChange = (visible: boolean) => {
    if (!visible && isCustom && !customSizesSelected.length && !customSizesSelected.width) {
      const selectedWithoutCustom = (selectedValue as SelectedValue).filter(item => item.value !== "custom");

      setSelectedValue(selectedWithoutCustom);
      setCustomSizesSelected(DEFAULT_SIZES);
      onChange && onChange(selectedWithoutCustom);
    }
  };

  const renderSpaceSizeInputs = (menu: ReactNode) => (
    <div className="locl-space-dropdown__menu" data-testid="locl-space-size-dropdown-inputs">
      { menu }
      <div className={styles["locl-space-dropdown__inputs-group"]}>
        <InputNumber
          fullWidth
          name="width"
          value={customSizesSelected.width}
          onChange={e => handleCustomChange("width", e as string)}
          placeholder="Width"
          data-testid="locl-space-size-dropdown-inputs-width"
        />
        <InputNumber
          fullWidth
          name="length"
          value={customSizesSelected.length}
          onChange={e => handleCustomChange("length", e as string)}
          placeholder="Length"
          data-testid="locl-space-size-dropdown-inputs-length"
        />
      </div>
    </div>
  );


  return (
    <Dropdown
      menu={{ items: SPACE_SIZES }}
      isMulti
      // eslint-disable-next-line no-undefined
      dropdownRender={isCustom ? renderSpaceSizeInputs : undefined}
      value={selectedValue}
      onChange={(e) => handleChange(e as SelectedValue)}
      onOpenChange={onOpenChange}
      onClick={onClick}
      disabled={disabled}
      placeholder="Space size"
      className={className}
      rootClassName={`${styles["locl-space-dropdown"]} ${overlayClassName}`}
      data-testid="locl-space-size-dropdown"
      buttonProps={buttonProps}
    >
      { children }
    </Dropdown>
  );
}

export default SpaceSizeDropdown;
